import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useWorkflowLifecycleStore } from '@/stores/workflow-lifecycle-store'
import { workflowLifecycleService } from '@/services/workflow-lifecycle'
import {
  downloadWorkflowExport,
  slugifyWorkflowName,
} from '@/lib/workflows/export-import'
import { useWorkflowStore } from '@/stores/workflow-store'
import type { WorkflowVersion } from '@/schemas/workflow'
import { VersionRow } from './version-row'
import { VersionDiffDrawer } from './version-diff-drawer'

interface HistoryTabProps {
  projectId: string
  workflowId: string
  onPublish?: () => void
}

export function HistoryTab({
  projectId,
  workflowId,
  onPublish,
}: HistoryTabProps) {
  const { t } = useTranslation('workflows')
  const {
    versions,
    versionsLoading,
    versionsError,
    fetchVersions,
    restoreVersion,
  } = useWorkflowLifecycleStore()
  const { currentWorkflow } = useWorkflowStore()

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [fromVersion, setFromVersion] = useState<number | null>(null)
  const [toVersion, setToVersion] = useState<number | null>(null)

  useEffect(() => {
    if (projectId && workflowId) {
      fetchVersions(projectId, workflowId)
    }
  }, [projectId, workflowId, fetchVersions])

  // Versions arrive in descending chronological order from the backend
  // (03-01 ordered by created_at desc), but normalize defensively.
  const ordered = [...versions].sort(
    (a, b) => b.version_number - a.version_number
  )
  const latest = ordered[0]

  const openDiffVsPrevious = useCallback(
    (v: WorkflowVersion) => {
      const idx = ordered.findIndex(
        (x) => x.version_number === v.version_number
      )
      const previous = ordered[idx + 1]
      if (previous) {
        setFromVersion(previous.version_number)
        setToVersion(v.version_number)
      } else {
        // First version — compare to itself (planner-documented behavior).
        setFromVersion(v.version_number)
        setToVersion(v.version_number)
      }
      setDrawerOpen(true)
    },
    [ordered]
  )

  const openDiffVsLatest = useCallback(
    (v: WorkflowVersion) => {
      if (!latest) return
      setFromVersion(v.version_number)
      setToVersion(latest.version_number)
      setDrawerOpen(true)
    },
    [latest]
  )

  const handleRestore = useCallback(
    async (versionNumber: number) => {
      const ok = await restoreVersion(projectId, workflowId, versionNumber)
      if (ok) {
        toast.success(t('lifecycle.restore.successNew', { n: versionNumber }))
      } else {
        toast.error(t('lifecycle.restore.failure'))
      }
    },
    [projectId, workflowId, restoreVersion, t]
  )

  const handleExport = useCallback(
    async (versionNumber: number) => {
      try {
        const data = await workflowLifecycleService.exportVersion(
          projectId,
          workflowId,
          versionNumber
        )
        const slug = slugifyWorkflowName(currentWorkflow?.name ?? 'workflow')
        downloadWorkflowExport(data, `${slug}-v${versionNumber}`)
        toast.success(t('lifecycle.export.success'))
      } catch {
        toast.error(t('lifecycle.export.failure'))
      }
    },
    [projectId, workflowId, currentWorkflow, t]
  )

  // ----- render states -----

  if (versionsLoading) {
    return (
      <div className="space-y-2" data-testid="history-tab">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            data-testid="version-skeleton"
            className="h-[56px] rounded-lg border border-border glass animate-pulse"
            role="status"
            aria-label={t('lifecycle.history.loading')}
          />
        ))}
      </div>
    )
  }

  if (versionsError) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-3 py-12"
        data-testid="history-tab"
      >
        <p className="text-sm text-muted-foreground">{versionsError}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchVersions(projectId, workflowId)}
        >
          {t('lifecycle.diff.retry')}
        </Button>
      </div>
    )
  }

  if (ordered.length === 0) {
    return (
      <div
        className="relative flex flex-col items-center justify-center gap-6 py-20 overflow-hidden"
        data-testid="history-tab-empty"
      >
        {/* Decorative outlined glyph */}
        <div
          aria-hidden
          className="pointer-events-none select-none font-mono font-bold leading-none text-transparent"
          style={{
            fontSize: 'clamp(180px, 28vw, 320px)',
            WebkitTextStroke: '1px hsl(var(--border))',
            letterSpacing: '-0.05em',
            opacity: 0.6,
          }}
        >
          v0
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
          <span className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground/70">
            — linha do tempo —
          </span>
          <h3 className="text-xl font-semibold text-foreground">
            {t('lifecycle.history.empty.title')}
          </h3>
          <p className="text-sm text-muted-foreground max-w-md leading-relaxed">
            {t('lifecycle.history.empty.body')}
          </p>
          {onPublish && (
            <Button
              variant="action-indigo"
              onClick={onPublish}
              className="mt-2"
            >
              {t('lifecycle.publish.cta')}
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div data-testid="history-tab">
      <div className="flex items-baseline justify-between pl-14 pr-4 pb-3 border-b border-border/60">
        <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground/70">
          Linha do tempo
        </span>
        <span className="text-[11px] tabular-nums text-muted-foreground/60">
          {ordered.length} {ordered.length === 1 ? 'versão' : 'versões'}
        </span>
      </div>
      <div className="relative">
        {ordered.map((v, i) => (
          <VersionRow
            key={v.id}
            version={v}
            isLatest={
              latest?.version_number === v.version_number &&
              currentWorkflow?.status === 'active'
            }
            isFirst={i === 0}
            isLast={i === ordered.length - 1}
            onDiff={openDiffVsPrevious}
            onDiffVsLatest={openDiffVsLatest}
            onRestore={(ver) => handleRestore(ver.version_number)}
            onExport={(ver) => handleExport(ver.version_number)}
          />
        ))}
      </div>

      <VersionDiffDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        projectId={projectId}
        workflowId={workflowId}
        fromVersion={fromVersion}
        toVersion={toVersion}
        versions={ordered}
        onChangeVersions={(from, to) => {
          setFromVersion(from)
          setToVersion(to)
        }}
        onRestore={handleRestore}
        onExport={handleExport}
      />
    </div>
  )
}
